import { Icon, type IconNames, Image, Price } from '@petsmart-ui/sparky';
import cn from 'classnames';
import { useEffect, useRef } from 'react';
import Highlighter from 'react-highlight-words';

import { decodeEntityString, getOptionsFromPrice, getPrices, getPriceVariant, isPriceRange } from '@libs/algolia';
import { useDecisions } from '@libs/common-next';

import type { ListItemProps, PanelItemProps } from '../types';

type AlgoliaHighlightQuery = {
    matchedWords: string[];
};

type AlgoliaHighlightMatchLevel = {
    matchLevel: string;
};

function ListItem({ sourceId, item }: ListItemProps) {
    const iconNames: Record<string, IconNames> = {
        recentSearchesPlugin: 'clock-outline',
        querySuggestionsPlugin: 'search',
    };

    const itemName = item.name ?? item.label ?? item.objectID;
    const matchLevel = (item?._highlightResult?.query as AlgoliaHighlightMatchLevel)?.matchLevel ?? 'none';

    const itemNameAsString: string = decodeURIComponent(encodeURI(itemName as string));

    return (
        <div className="autocomplete-list-item__button-content" data-testid="list-item-suggestion">
            <div className="autocomplete-list-item__main">
                {iconNames[sourceId] && <Icon className="autocomplete-list-item__icon" name={iconNames[sourceId]} />}

                {itemName && (
                    <span className="autocomplete-list-item__name" data-testid="item-name">
                        <Highlighter
                            highlightClassName="normal-text"
                            unhighlightClassName={matchLevel !== 'none' ? 'highlighted-text' : 'normal-text'}
                            searchWords={(item?._highlightResult?.query as AlgoliaHighlightQuery)?.matchedWords ?? []}
                            textToHighlight={itemNameAsString}
                        />
                    </span>
                )}

                {(item.__autocomplete_qsCategory as string) &&
                    !(item.__autocomplete_qsCategory as string).includes('+') && (
                        <span className="autocomplete-list-item__category">
                            — in {item.__autocomplete_qsCategory as string}
                        </span>
                    )}
            </div>

            <Icon className="autocomplete-list-item__icon__arrow autocomplete-assist-search" name="navigation-arrow" />
        </div>
    );
}

function ProductItem({ item }: Readonly<Pick<PanelItemProps, 'item'>>) {
    const dropdownPriceDecision = useDecisions('x_drpdwn_price');
    const imageUrl = item.images?.small ?? item.images?.large;
    const isRange = isPriceRange(item.priceData);
    const shouldUseOptionsFrom = dropdownPriceDecision === 'options_from' && isRange;
    const optionsFromPrice = shouldUseOptionsFrom ? getOptionsFromPrice(item.priceData) : undefined;
    let priceVariant = getPriceVariant(item.priceData);
    if (optionsFromPrice) {
        priceVariant = optionsFromPrice.priceVariant === 'disabled' ? 'disabled' : 'primary';
    }

    return (
        <div className="autocomplete-list-item__product" data-testid="product-item-suggestion">
            {imageUrl && <Image src={imageUrl} alt={`${item.name} image`} loading="lazy" width={64} height={64} />}
            <div
                className={cn('autocomplete-list-item__main', {
                    'autocomplete-list-item__main--options-from': shouldUseOptionsFrom,
                })}
            >
                <div className="autocomplete-list-item__product-name">{decodeEntityString(item.name)}</div>
                {dropdownPriceDecision !== 'no_price' && (
                    <Price
                        className={cn('autocomplete-list-item__price', {
                            'autocomplete-list-item__price--options-from': shouldUseOptionsFrom,
                        })}
                        info={shouldUseOptionsFrom ? 'Options from' : undefined}
                        isRange={shouldUseOptionsFrom ? false : isRange}
                        values={optionsFromPrice?.prices ?? getPrices(item.priceData, isRange)}
                        variant={priceVariant}
                    />
                )}
            </div>
        </div>
    );
}

export function PanelItem({
    className,
    onKeyDown,
    focus,
    item,
    sourceId,
    onClick,
    ...other
}: Readonly<PanelItemProps>) {
    const handleKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
        onKeyDown(e);
        if (e.key === 'Enter') {
            e.currentTarget.click();
        }
    };

    const buttonRef = useRef<HTMLButtonElement>(null);
    useEffect(() => {
        if (focus) {
            buttonRef.current?.focus();
        } else {
            buttonRef.current?.blur();
        }
    }, [focus]);

    const liClassName = cn('autocomplete-list-item', className);

    return (
        <li className={liClassName} data-testid="suggestion-item" {...other}>
            <button
                data-testid="suggestion-item-button"
                onClick={onClick}
                className="autocomplete-list-item__button"
                type="submit"
                onKeyDown={handleKeyDown}
                ref={buttonRef}
            >
                {sourceId !== 'productSuggestionsPlugin' ? (
                    <ListItem sourceId={sourceId} item={item} />
                ) : (
                    <ProductItem item={item} />
                )}
            </button>
        </li>
    );
}
